import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

import type { MFAMethod } from "../../api";
import type { LoginOutcome } from "../../api/api-connector";

import { apiPutUsersChangePassword } from "../../api";
import { ApiConnector, RequestError } from "../../api/api-connector";
import { extractDetailMessage } from "../../app/error-text";
import { Button } from "../../components/ui-kit/button";
import { Dialog } from "../../components/ui-kit/dialog";
import { useDialog } from "../../components/ui-kit/dialog-provider";
import { PasswordInput } from "../../components/ui-kit/fields/password-input";
import { Form } from "../../components/ui-kit/form/form";
import { FormRow } from "../../components/ui-kit/form/form-row";
import { FormTable } from "../../components/ui-kit/form/form-table";
import { MfaVerification } from "../auth/mfa/mfa-verification";

export function ChangePasswordDialog({
  api,
  onClose,
}: {
  api: ApiConnector;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const { alert } = useDialog();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [challenge, setChallenge] = useState<{
    id: string;
    methods: MFAMethod[];
  }>();

  const handleSubmit = async () => {
    // Validate passwords match
    if (newPassword !== repeatPassword) {
      await alert(t`New passwords do not match`);
      return;
    }

    let outcome: LoginOutcome;

    try {
      // First, relogin with old password to get a fresh access token
      outcome = await api.relogin(oldPassword);
    } catch (error) {
      await reportFailure(error);
      return;
    }

    // The password was only the first factor. Asked for here rather than left
    // to the 428, which would send the user through a second password entry to
    // reach the same screen. Anything else is left to it: an account that owes
    // an enrollment can't finish one from a dialog.
    if (outcome.status === "mfa_required") {
      setChallenge({ id: outcome.challenge_id, methods: outcome.methods });
      return;
    }

    await changePassword();
  };

  async function changePassword() {
    try {
      await apiPutUsersChangePassword({
        body: {
          old_password: oldPassword,
          new_password: newPassword,
          repeat_password: repeatPassword,
        },
        meta: { rotateOn401: "never" },
      });

      setChallenge(undefined);
      setSuccess(true);
    } catch (error) {
      // Back to the form, which is where the reason makes sense.
      setChallenge(undefined);
      await reportFailure(error);
    }
  }

  async function reportFailure(error: unknown) {
    if (!(error instanceof RequestError)) {
      throw error;
    }

    if (error.response.status === 401) {
      await alert(t`Incorrect current password`);
    } else if (
      error.data &&
      typeof error.data === "object" &&
      "detail" in error.data
    ) {
      await alert(extractDetailMessage(error.data));
    } else {
      await alert(t`Failed to change password`);
    }
  }

  if (challenge) {
    return (
      <Dialog open onOpenChange={onClose}>
        <MfaVerification
          api={api}
          challengeId={challenge.id}
          methods={challenge.methods}
          onVerified={() => {
            void changePassword();
          }}
          onCancel={onClose}
          cancelLabel={<Trans>Cancel</Trans>}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <div className="flex flex-col gap-4">
        {success ? (
          <>
            <h2 className="text-base font-semibold">
              <Trans>Password Changed Successfully</Trans>
            </h2>
            <p>
              <Trans>Your password has been changed successfully.</Trans>
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <h2 className="text-base font-semibold mb-4">
              <Trans>Change password</Trans>
            </h2>

            <FormTable dataAdd>
              <FormRow
                label={<Trans>Current password</Trans>}
                data={
                  <PasswordInput
                    autofocus
                    value={oldPassword}
                    onChange={setOldPassword}
                    required
                  />
                }
              />

              <FormRow
                label={<Trans>New password</Trans>}
                data={
                  <PasswordInput
                    value={newPassword}
                    onChange={setNewPassword}
                    required
                  />
                }
              />

              <FormRow
                label={<Trans>Repeat new password</Trans>}
                data={
                  <PasswordInput
                    value={repeatPassword}
                    onChange={setRepeatPassword}
                    required
                  />
                }
              />
            </FormTable>

            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit">
                <Trans>Change password</Trans>
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Dialog>
  );
}
